import { ApolloQueryResult } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Form, { FormApi, FormFooter, FormSection } from '@atlaskit/form';
import get from 'lodash/get';
import set from 'lodash/set';
import React from 'react';
import { useDispatch } from 'react-redux';
import { OrderRequestMetaData, UpdateOrderRequest } from '@atlassian/cssxp-typescommon';
import {
  actions as SidebarActions,
  InfoPanel,
  ErrorComponents,
  InvoiceGroupSelectItem,
  useFormSubmit,
  useNotification,
  orderSuccessNotification,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import { updateEntitlementPlan } from '@atlassian/temp-proxies';
import { EntitlementChangePlanForm } from './change-plan-form-field';
import { OfferingSelectItem } from './entitlement-change-plan-pane';
import { withApollo } from '@css-xp/base/graphql-provider';
import { analytics, AnalyticsTrackAction, AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
interface Props {
  entitlement: EntitlementDetails;
  transactionAccountContext: TransactionAccountContextType;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

interface FormData {
  entitlementId?: string;
  billingFrequency?: string;
  offeringSelectedItem?: OfferingSelectItem;
  pricingPlan?: string;
  supportTicketNumber?: string;
  invoiceGroupId?: InvoiceGroupSelectItem;
}

export const ChangePricingPlanPane = withApollo(({ entitlement, transactionAccountContext, refetch }: Props) => {
  const { getTransactionAccount, data: transactionAccountData } = transactionAccountContext;

  const dispatch = useDispatch();

  const { error, isProcessing, submitForm, complete, data } = useFormSubmit<UpdateOrderRequest, any>({
    submitFormAction: updateEntitlementPlan,
    onComplete: () => {
      dispatch(SidebarActions.closeSidebar());
      refetch();
      analytics.sendTrackEvent({
        attributes: {
          ticketNumber: data.supportTicketNumber,
        },
        action: AnalyticsTrackAction.UPDATED,
        actionSubject: AnalyticsTrackSubject.CHANGE_PLAN,
      });
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  const handleFormSubmit = (formData: FormData, formApi: FormApi<FormData>) => {
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(formData, key);
      set(transformedData, key, value);
    });
    const requestData = {
      entitlementId: entitlement.id,
      billingFrequency: entitlement.billingFrequency ?? 'MONTHLY',
      offeringId: formData.offeringSelectedItem?.value.id ?? '',
      invoiceGroupId: transformedData?.invoiceGroupId?.value ?? '',
      pricingPlanType: get(formData, 'pricingPlan'),
      metadata: {
        [OrderRequestMetaData.SUPPORT_TICKET_NUMBER]: formData.supportTicketNumber ?? null,
      },
      productId: entitlement.productId ?? '',
    };
    submitForm(requestData);
  };

  const transactionAccount = getTransactionAccount(transactionAccountData);

  if (complete && !notificationSent) {
    const transitionTimeError =
      data.transitionTime === 'unknown'
        ? ' - We were unable to determine if the successfully submitted order is immediate or scheduled.'
        : undefined;

    const notificationOptions = orderSuccessNotification(
      'CHANGE_PRICING_PLAN',
      data.transitionTime,
      transitionTimeError
    );
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  return (
    <div className="entitlement-change-plan-form" style={{ maxWidth: '600px' }}>
      <h3>Change pricing</h3>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormSection>
              {error && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to change the pricing plan for the following reason"
                      message={error}
                    />
                  }
                />
              )}
            </FormSection>
            <EntitlementChangePlanForm
              entitlement={entitlement}
              transactionAccount={transactionAccount}
              pricingPlanFlag={true}
            />
            <FormFooter align="start">
              <ButtonGroup>
                <LoadingButton testId="submit-btn" appearance="primary" type="submit" isLoading={isProcessing}>
                  Change pricing
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
});
